import * as React from 'react';
import type { Editor } from '@tiptap/react';
import { CheckIcon, ListChecksIcon, XIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { inlineChangeAt, type InlineChangeTarget } from './document-inline-review';
import { useEditorDomListener } from './editor-dom-events';

type MenuState = { open: false } | { open: true; change: InlineChangeTarget; x: number; y: number };
export type TrackedChangeDecision = 'accept' | 'reject';

/**
 * Right-click (or the keyboard context-menu key) on a pending inline insertion or deletion opens a
 * small menu to accept or reject that one change. Elsewhere the browser's own menu is kept, and
 * menus already opened by spelling or AI proofing (which prevent the event) take precedence.
 */
export function TrackedChangeContextMenu({ editor, enabled, canDecide, authorName, onDecide, onShowAll }: {
  editor: Editor;
  /** False hides the menu entirely, for example when the saved review cannot be read. */
  enabled: boolean;
  /** False keeps the menu readable but disables decisions while a save or review request is pending. */
  canDecide: boolean;
  authorName: (changeId: string) => string | undefined;
  onDecide: (changeId: string, decision: TrackedChangeDecision) => void;
  onShowAll: () => void;
}) {
  const [menu, setMenu] = React.useState<MenuState>({ open: false });
  const onContextMenu = React.useCallback((event: MouseEvent) => {
    if (event.defaultPrevented || editor.isDestroyed) return;
    const dom = editor.view.dom;
    const element = event.target instanceof Element ? event.target.closest('[data-inline-review]') : null;
    let change = element && dom.contains(element) ? inlineChangeAt(editor.state, editor.view.posAtDOM(element, 0)) : null;
    let x = event.clientX, y = event.clientY;
    // Keyboard-opened menus have no pointer position: use the caret instead.
    if (!change && event.button !== 2 && x === 0 && y === 0) {
      const { from } = editor.state.selection;
      change = inlineChangeAt(editor.state, from);
      if (change) { const coords = editor.view.coordsAtPos(from); x = coords.left; y = coords.bottom; }
    }
    if (!change) return;
    event.preventDefault();
    setMenu({ open: true, change, x, y });
  }, [editor]);
  useEditorDomListener(editor, 'contextmenu', enabled ? onContextMenu : undefined);
  React.useEffect(() => { if (!enabled) setMenu({ open: false }); }, [enabled]);

  if (!menu.open) return null;
  const { change } = menu, insertion = change.kind === 'insertion';
  const author = authorName(change.id) || 'Unknown person';
  const close = () => setMenu({ open: false });
  const decide = (decision: TrackedChangeDecision) => { onDecide(change.id, decision); close(); };

  return <DropdownMenu open onOpenChange={open => { if (!open) close(); }} modal={false}>
    <DropdownMenuTrigger asChild>
      <span aria-hidden="true" className="pointer-events-none fixed size-0" style={{ left: menu.x, top: menu.y }} />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" side="bottom" sideOffset={2} className="w-60" aria-label="Tracked change">
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{(insertion ? 'Added by ' : 'Removed by ') + author}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem disabled={!canDecide} onSelect={() => decide('accept')}><CheckIcon aria-hidden /> {insertion ? 'Accept addition' : 'Accept removal'}</DropdownMenuItem>
      <DropdownMenuItem disabled={!canDecide} onSelect={() => decide('reject')}><XIcon aria-hidden /> Reject change</DropdownMenuItem>
      {!canDecide && <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Save or finish the current review action first.</DropdownMenuLabel>}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => { onShowAll(); close(); }}><ListChecksIcon aria-hidden /> Show all tracked changes</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>;
}
